/**
 * A bought service's supplier and cost (supplier interface spec 12): the
 * supplier searched at the URL the order view carries, and its cost entered
 * by hand. Shared by Add extra (a fulfilment or installation bought from a
 * supplier) and *Change supplier…* on a bought service. The server's refusal
 * of either (a supplier with no goods-in address, one on hold, a cost of
 * nothing) arrives bound to its field.
 */
import { useCallback } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import {
  EntityCombobox,
  type EntityOption,
} from "../../patterns/EntityCombobox";
import { Input } from "../../primitives/input";
import { Field } from "./OrderDialogs";

export const CHOOSE_SUPPLIER = "Choose the supplier.";
export const ENTER_COST = "Enter the supplier's cost in pounds, e.g. 30.00.";
/** Shown once a supplier is chosen for a service on print: orders sends the print there. */
export const SERVICE_GOODS_HINT =
  "The print it's for goes to this supplier's goods-in.";

export function ServiceSupplierFields({
  idPrefix,
  suppliersUrl,
  supplier,
  onSupplierChange,
  cost,
  onCostChange,
  supplierError,
  costError,
  goodsHint,
  disabled,
}: {
  idPrefix: string;
  /** The order view's search URL (`add_extra.suppliers_url`, `change_supplier.suppliers_url`). */
  suppliersUrl: string;
  supplier: EntityOption | null;
  onSupplierChange: (supplier: EntityOption | null) => void;
  /** The cost as typed, in pounds. */
  cost: string;
  onCostChange: (cost: string) => void;
  supplierError?: string;
  costError?: string;
  /** Where the print goes once a supplier is chosen, when the service takes some. */
  goodsHint?: string;
  disabled?: boolean;
}) {
  const adapter = useDataAdapter();
  const fetchSuppliers = useCallback(
    async (q: string): Promise<EntityOption[]> =>
      // A command names the supplier by its number (SUP-…); one the purchase ledger has no number for can't be named.
      (await adapter.searchServiceSuppliers(suppliersUrl, q)).flatMap((s) =>
        s.supplier_no
          ? [
              {
                id: s.supplier_no,
                label: s.name ?? s.supplier_no,
                hint: s.supplier_no,
              },
            ]
          : [],
      ),
    [adapter, suppliersUrl],
  );
  const supplierErrorId = `${idPrefix}-supplier-error`;
  const supplierHintId = `${idPrefix}-supplier-hint`;
  const showHint = Boolean(supplier && goodsHint && !supplierError);
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="space-y-1">
        {/* The combobox names itself ("Supplier"); this is its visible caption. */}
        <p aria-hidden className="text-sm font-medium">
          Supplier
        </p>
        <EntityCombobox
          value={supplier}
          onChange={onSupplierChange}
          fetchOptions={fetchSuppliers}
          placeholder="Search suppliers"
          ariaLabel="Supplier"
          disabled={disabled}
          aria-invalid={supplierError ? true : undefined}
          aria-describedby={
            supplierError
              ? supplierErrorId
              : showHint
                ? supplierHintId
                : undefined
          }
        />
        {showHint && (
          <p id={supplierHintId} className="text-xs text-muted-foreground">
            {goodsHint}
          </p>
        )}
        {supplierError && (
          <p id={supplierErrorId} className="text-sm text-red-600">
            {supplierError}
          </p>
        )}
      </div>
      <Field
        id={`${idPrefix}-cost`}
        label="Supplier's cost, ex VAT (£)"
        error={costError}
      >
        {(aria) => (
          <Input
            {...aria}
            inputMode="decimal"
            value={cost}
            placeholder="30.00"
            disabled={disabled}
            onChange={(e) => onCostChange(e.target.value)}
          />
        )}
      </Field>
    </div>
  );
}
